import React, { useEffect, useState } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    Form,
    Button,
    Table,
    Image,
    Modal,
    Spinner,
    Alert,
    Badge,
} from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import axios from "axios";

const ProductCategoryPage = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(null);

    const [showModal, setShowModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);

    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [errors, setErrors] = useState({});

    const [name, setName] = useState("");
    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    const [children, setChildren] = useState([]);

    const getAuthHeaders = () => {
        const token = localStorage.getItem("authToken");
        return {
            Authorization: token ? `Bearer ${token}` : "",
        };
    };

    const getCategories = async () => {
        try {
            setLoading(true);
            setError("");

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/web-manager/product-categories`,
                { headers: getAuthHeaders() }
            );

            const data = response.data?.data || response.data || [];
            setCategories(data);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to load categories.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getCategories();
    }, []);

    const getImageUrl = (path) => {
        if (!path) return "";
        if (path.startsWith("http://") || path.startsWith("https://")) return path;
        return `${process.env.REACT_APP_IMG_URL}/${path}`;
    };

    const resetForm = () => {
        setName("");
        setImage(null);
        setImagePreview(null);
        setChildren([]);
        setEditingCategory(null);
        setErrors({});
        setError("");
    };

    const openAddModal = () => {
        resetForm();
        setShowModal(true);
    };

    const openEditModal = (category) => {
        setEditingCategory(category);
        setName(category.name || "");
        setImage(null);
        setImagePreview(getImageUrl(category.image));

        const existingChildren = (category.child_categories || category.childCategories || []).map((child) => ({
            id: child.id,
            name: child.name || "",
            image: null,
            preview: getImageUrl(child.image),
        }));

        setChildren(existingChildren);
        setErrors({});
        setError("");
        setShowModal(true);
    };

    const closeModal = () => {
        if (!saving) {
            setShowModal(false);
            resetForm();
        }
    };

    const handleParentImageChange = (e) => {
        const file = e.target.files?.[0] || null;
        setImage(file);
        if (file) {
            setImagePreview(URL.createObjectURL(file));
        }
        setErrors((prev) => ({ ...prev, image: "" }));
    };

    const handleAddChildRow = () => {
        setChildren((prev) => [
            ...prev,
            { id: null, name: "", image: null, preview: null },
        ]);
    };

    const handleRemoveChildRow = (index) => {
        setChildren((prev) => prev.filter((_, i) => i !== index));
    };

    const handleChildNameChange = (index, value) => {
        setChildren((prev) => {
            const updated = [...prev];
            updated[index].name = value;
            return updated;
        });
    };

    const handleChildImageChange = (index, file) => {
        setChildren((prev) => {
            const updated = [...prev];
            updated[index].image = file || null;
            updated[index].preview = file ? URL.createObjectURL(file) : null;
            return updated;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage("");
        setError("");

        try {
            setSaving(true);
            setErrors({});

            const formData = new FormData();
            formData.append("name", name.trim());

            if (image) {
                formData.append("image", image);
            }

            children.forEach((child, index) => {
                if (child.id) {
                    formData.append(`children[${index}][id]`, child.id);
                }
                formData.append(`children[${index}][name]`, child.name.trim());
                if (child.image) {
                    formData.append(`children[${index}][image]`, child.image);
                }
            });

            if (editingCategory) {
                formData.append("_method", "PUT");
                const response = await axios.post(
                    `${process.env.REACT_APP_API_URL}/web-manager/product-categories/${editingCategory.id}`,
                    formData,
                    { headers: getAuthHeaders() }
                );
                setMessage(response.data?.message || "Category updated successfully.");
            } else {
                const response = await axios.post(
                    `${process.env.REACT_APP_API_URL}/web-manager/product-categories`,
                    formData,
                    { headers: getAuthHeaders() }
                );
                setMessage(response.data?.message || "Category created successfully.");
            }

            setShowModal(false);
            resetForm();
            await getCategories();
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors || {});
                setError(err.response.data.message || "Validation errors");
            } else {
                setError(err.response?.data?.message || "Something went wrong.");
            }
        } finally {
            setSaving(false);
        }
    };

    const deleteCategory = async (id) => {
        if (!window.confirm("Are you sure? This will delete the category and all its subcategories.")) return;

        try {
            setDeleting(id);
            setError("");
            setMessage("");

            const response = await axios.delete(
                `${process.env.REACT_APP_API_URL}/web-manager/product-categories/${id}`,
                { headers: getAuthHeaders() }
            );

            setMessage(response.data?.message || "Category deleted successfully.");
            await getCategories();
        } catch (err) {
            setError(err.response?.data?.message || "Failed to delete category.");
        } finally {
            setDeleting(null);
        }
    };

    return (
        <PageLayout>
            <Container fluid className="py-4">
                <Row className="align-items-center mb-4">
                    <Col>
                        <h2 className="fw-bold mb-1">Product Categories</h2>
                        <p className="text-muted mb-0">Manage parent and child product categories.</p>
                    </Col>
                    <Col xs="auto">
                        <Button variant="primary" onClick={openAddModal}>
                            + Add Category
                        </Button>
                    </Col>
                </Row>

                {message && (
                    <Alert variant="success" dismissible onClose={() => setMessage("")}>
                        {message}
                    </Alert>
                )}

                {error && !showModal && (
                    <Alert variant="danger" dismissible onClose={() => setError("")}>
                        {error}
                    </Alert>
                )}

                <Card className="border-0 shadow-sm">
                    <Card.Body className="p-0">
                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" />
                                <div className="mt-3 text-muted">Loading categories...</div>
                            </div>
                        ) : categories.length === 0 ? (
                            <div className="text-center py-5">
                                <h5>No categories found.</h5>
                                <Button variant="primary" className="mt-2" onClick={openAddModal}>
                                    + Add Category
                                </Button>
                            </div>
                        ) : (
                            <Table responsive hover className="mb-0 align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th style={{ width: "80px" }}>#</th>
                                        <th style={{ width: "100px" }}>Image</th>
                                        <th>Category Name</th>
                                        <th>Subcategories</th>
                                        <th className="text-end" style={{ width: "200px" }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {categories.map((cat, index) => {
                                        const subCategories = cat.child_categories || cat.childCategories || [];
                                        return (
                                            <tr key={cat.id || index}>
                                                <td>{index + 1}</td>
                                                <td>
                                                    {cat.image ? (
                                                        <Image
                                                            src={getImageUrl(cat.image)}
                                                            width={50}
                                                            height={50}
                                                            rounded
                                                            alt={cat.name}
                                                            style={{ objectFit: "cover" }}
                                                        />
                                                    ) : (
                                                        <span className="text-muted small">No Image</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <span className="fw-bold text-dark">{cat.name}</span>
                                                </td>
                                                <td>
                                                    {subCategories.length > 0 ? (
                                                        <div className="d-flex flex-wrap gap-1">
                                                            {subCategories.map((child) => (
                                                                <Badge bg="light" text="dark" className="border" key={child.id}>
                                                                    {child.name}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted small">None</span>
                                                    )}
                                                </td>
                                                <td className="text-end">
                                                    <Button
                                                        size="sm"
                                                        variant="outline-primary"
                                                        className="me-2"
                                                        onClick={() => openEditModal(cat)}
                                                    >
                                                        Edit
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="danger"
                                                        disabled={deleting === cat.id}
                                                        onClick={() => deleteCategory(cat.id)}
                                                    >
                                                        {deleting === cat.id ? "Deleting..." : "Delete"}
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        )}
                    </Card.Body>
                </Card>

                <Modal show={showModal} onHide={closeModal} centered size="lg" backdrop="static">
                    <Form onSubmit={handleSubmit}>
                        <Modal.Header closeButton className="border-0 pb-0 pt-4 px-4">
                            <Modal.Title className="fw-bold fs-4">
                                {editingCategory ? "Edit Category" : "Add Category"}
                            </Modal.Title>
                        </Modal.Header>

                        <Modal.Body className="p-4">
                            {error && <Alert variant="danger">{error}</Alert>}

                            <Row className="mb-4">
                                <Col md={8}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold text-uppercase text-muted small mb-2">
                                            Parent Category Name
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={name}
                                            onChange={(e) => {
                                                setName(e.target.value);
                                                setErrors((prev) => ({ ...prev, name: "" }));
                                            }}
                                            isInvalid={!!errors.name}
                                            className="bg-light border-0 py-2 px-3"
                                            placeholder="e.g. Men's Fashion"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {Array.isArray(errors.name) ? errors.name[0] : errors.name}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold text-uppercase text-muted small mb-2">
                                            Category Image
                                        </Form.Label>
                                        <Form.Control
                                            type="file"
                                            accept=".jpg,.jpeg,.png,.webp"
                                            onChange={handleParentImageChange}
                                            isInvalid={!!errors.image}
                                            className="bg-light border-0 py-2 px-3"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {Array.isArray(errors.image) ? errors.image[0] : errors.image}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    {imagePreview && (
                                        <Image
                                            src={imagePreview}
                                            rounded
                                            style={{ width: "60px", height: "60px", objectFit: "cover" }}
                                        />
                                    )}
                                </Col>
                            </Row>

                            <hr />

                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <h6 className="fw-bold mb-0">Subcategories (Children)</h6>
                                <Button size="sm" variant="outline-primary" onClick={handleAddChildRow}>
                                    + Add Subcategory
                                </Button>
                            </div>

                            {children.map((child, idx) => (
                                <Row key={idx} className="align-items-center mb-3 bg-light p-2 rounded mx-0">
                                    <Col md={5}>
                                        <Form.Control
                                            type="text"
                                            placeholder="Subcategory Name"
                                            value={child.name}
                                            onChange={(e) => handleChildNameChange(idx, e.target.value)}
                                            isInvalid={!!errors[`children.${idx}.name`]}
                                            className="border-0 bg-white"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors[`children.${idx}.name`]}
                                        </Form.Control.Feedback>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Control
                                            type="file"
                                            accept=".jpg,.jpeg,.png,.webp"
                                            onChange={(e) => handleChildImageChange(idx, e.target.files?.[0])}
                                            className="border-0 bg-white"
                                        />
                                    </Col>

                                    <Col md={1} className="text-center">
                                        {child.preview && (
                                            <Image
                                                src={child.preview}
                                                rounded
                                                style={{ width: "35px", height: "35px", objectFit: "cover" }}
                                            />
                                        )}
                                    </Col>

                                    <Col md={2} className="text-end">
                                        <Button
                                            size="sm"
                                            variant="danger"
                                            onClick={() => handleRemoveChildRow(idx)}
                                        >
                                            Remove
                                        </Button>
                                    </Col>
                                </Row>
                            ))}
                        </Modal.Body>

                        <Modal.Footer className="border-0 pt-0 pb-4 px-4 justify-content-end">
                            <Button
                                variant="light"
                                onClick={closeModal}
                                disabled={saving}
                                className="px-4 py-2 text-uppercase fw-semibold bg-light text-secondary border-0 me-2"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={saving}
                                className="px-4 py-2 text-uppercase fw-semibold"
                            >
                                {saving ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Saving...
                                    </>
                                ) : editingCategory ? (
                                    "Update Category"
                                ) : (
                                    "Add Category"
                                )}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>
            </Container>
        </PageLayout>
    );
};

export default ProductCategoryPage;